import { Avatar, Box, Flex, Text, TextField } from '@radix-ui/themes';
import { CommonSearchInput } from 'components/common/form/search';
import { CommonHitterAvatar } from 'components/common/hitters/avatar';
import { HitterSideBadge } from 'components/common/hitters/hitter-side-badge';
import { HittersContext } from 'contexts/hitters.context';
import { CrudAction } from 'enums/tables';
import { t } from 'i18next';
import { IOptionExt } from 'interfaces/forms/option-ext';
import { StringHelper } from 'lib_ts/classes/string.helper';
import { HITTER_SIDES_DICT } from 'lib_ts/enums/hitters.enums';
import { PLAYER_LEVELS_DICT } from 'lib_ts/enums/pitches.enums';
import { IHitter } from 'lib_ts/interfaces/i-hitter';
import { compact, first } from 'lodash';
import { ReactNode, useCallback, useContext, useMemo } from 'react';

const CREATE_HITTER_ID = '----create----';
const LIST_MAX_WIDTH = '300px';
const MAX_NUM_RECENTLY_SELECTED = 10;
const RECENTLY_SELECTED_GROUP_LABEL = t('common.recently-selected').toString();

export const getHitterOption = (h: IHitter, group: string): IOptionExt => {
  const sideLabel = HITTER_SIDES_DICT[h.side]?.label;

  const o: IOptionExt = {
    label: h.name,
    value: h._id,
    group,
    suffixLabel: sideLabel ? t(sideLabel).toString() : undefined,
    suffix: <HitterSideBadge value={h.side} />,
    prefix: <CommonHitterAvatar size="1" hitter={h} clickDisabled />,
    aliases: compact([h.name, h._mx_phone, h._mx_phone?.replace(/\D/g, '')]), // allow searching by phone number
  };

  return o;
};

export const SearchHitter = () => {
  const { active, filtered, lastChanged, hitters, openCrudDialog, setActive } =
    useContext(HittersContext);

  const filteredOptions = useMemo(() => {
    const recentlySelected = filtered
      .sort((a, b) => {
        if (a.lastActive && b.lastActive) {
          return -StringHelper.safeSort(a.lastActive, b.lastActive);
        }

        if (a.lastActive) {
          return -1;
        }

        return 1;
      })
      .slice(0, MAX_NUM_RECENTLY_SELECTED)
      .filter((h) => !!h.lastActive) // we might have fewer than MAX_NUM_RECENTLY_SELECTED with a lastActive
      .map((h) => getHitterOption(h, RECENTLY_SELECTED_GROUP_LABEL));

    const nonRecentlySelectedOptions = filtered
      .filter((h) => !recentlySelected.find((r) => r.value === h._id))
      .map((h) => {
        const levelLabel = PLAYER_LEVELS_DICT[h.level]?.label;
        return getHitterOption(
          h,
          levelLabel ? `${t('common.level')}: ${t(levelLabel)}` : 'No Level'
        );
      })
      .sort((a, b) => StringHelper.safeSort(a.group, b.group));

    return [...recentlySelected, ...nonRecentlySelectedOptions];
  }, [filtered]);

  const renderTrigger = useCallback(
    (value: string[], slotIcon: ReactNode) => {
      const hitterID = first(value);

      if (!hitterID) {
        return (
          <Box position="relative" className="cursor-pointer">
            <TextField.Root
              style={{
                pointerEvents: 'none',
                position: 'absolute',
                width: '100%',
                height: '100%',
                zIndex: -1,
              }}
              variant="surface"
              onChange={() => {}}
            />
            <Flex align="center" gap="2" p="2">
              <Avatar
                fallback="N/A"
                size="2"
                // background color
                color="gray"
                style={{
                  // font color
                  color: 'var(--gray-a11)',
                }}
              />
              <Text truncate>{t('hitters.select-hitter')}</Text>
              <Flex
                align="center"
                ml="auto"
                onClick={(e) => {
                  e.stopPropagation();
                }}
              >
                {slotIcon}
              </Flex>
            </Flex>
          </Box>
        );
      }

      if (!hitters) {
        return;
      }

      const hitter = hitters.find((o) => o._id === hitterID);

      if (!hitter) {
        return;
      }

      // e.g. J. Smith, V. Guerrero Jr.
      const displayName = StringHelper.shortenName(hitter.name);

      return (
        <Box position="relative" className="cursor-pointer">
          <TextField.Root
            style={{
              pointerEvents: 'none',
              position: 'absolute',
              width: '100%',
              height: '100%',
              zIndex: -1,
            }}
            variant="surface"
            onChange={() => {}}
          />
          <Flex align="center" gap="2" p="2">
            <CommonHitterAvatar size="2" hitter={hitter} clickDisabled />
            <Text truncate mr="auto">
              {displayName}
            </Text>
            <HitterSideBadge value={hitter.side} />
            <Flex
              align="center"
              onClick={(e) => {
                e.stopPropagation();
                setActive();
              }}
            >
              {slotIcon}
            </Flex>
          </Flex>
        </Box>
      );
    },
    [active, hitters]
  );

  return (
    <CommonSearchInput
      key={lastChanged}
      id="hitters-hitter"
      placeholder="common.hitter"
      options={[
        {
          label: t('hitters.add-a-new-hitter'),
          value: CREATE_HITTER_ID,
        },
        ...filteredOptions,
      ]}
      values={active ? [active._id] : []}
      onChange={(v) => {
        if (v.length === 0) {
          setActive(undefined);
          return;
        }

        const hitterID = v[0];

        if (!hitterID) {
          return;
        }

        if (hitterID === CREATE_HITTER_ID) {
          openCrudDialog({
            key: Date.now(),
            action: CrudAction.Create,
            models: [],
          });
          return;
        }

        setActive(filtered.find((h) => h._id === hitterID)?._id);
      }}
      renderTrigger={renderTrigger}
      contentMaxWidth={LIST_MAX_WIDTH}
      strict
      matchDigits
      skipSort
      optional
    />
  );
};
